/**
 * The route table: the revised information architecture of pivot doc §3 as
 * data. The sidebar, section tabs, access gates and the router all read it, so
 * a page exists in exactly one place.
 *
 * Sections are landing pages with in-page tabs, one level deep. A section's
 * `entitlement` is the plan module it belongs to; its `permission` (and a
 * page's) is the RBAC permission the API enforces for the same data, so the
 * app hides what the server would refuse.
 */

import { matchPath } from 'react-router-dom'

import type { ModuleKey, Permission } from '@/api/types'
import type { IconName } from '@/design/Icon'

export type SectionKey =
  | 'overview'
  | 'insights'
  | 'analytics'
  | 'marketing'
  | 'automation'
  | 'competitions'
  | 'market'
  | 'reports'
  | 'integrations'
  | 'settings'

export interface TabDef {
  label: string
  path: string
  description: string
  permission?: Permission
  /** Active only on this exact path, not its sub-pages. */
  end?: boolean
}

export type PageKind = 'landing' | 'tab' | 'detail' | 'create' | 'redirect'

export interface PageDef {
  path: string
  title: string
  kind: PageKind
  description?: string
  permission?: Permission
  /** The tab this page sits under. */
  tab?: string
  redirectTo?: string
}

export interface SectionDef {
  key: SectionKey
  label: string
  icon: IconName
  /** Every route in the section starts with this. */
  prefix: string
  entitlement: ModuleKey | null
  permission: Permission | null
  description: string
  tabs: readonly TabDef[]
  pages: readonly PageDef[]
}

export interface RouteDef extends PageDef {
  section: SectionDef
}

function tabPages(tabs: readonly TabDef[], landing?: string): PageDef[] {
  return tabs.map((tab) => ({
    path: tab.path,
    title: tab.label,
    kind: tab.path === landing ? 'landing' : 'tab',
    description: tab.description,
    permission: tab.permission,
    tab: tab.path,
  }))
}

const INSIGHTS_TABS: TabDef[] = [
  { label: 'Digest', path: '/insights/digest', description: 'A written summary of what changed, why, and what to do next.' },
  { label: 'Recommendations', path: '/insights/recommendations', description: 'Suggested actions with estimated impact and confidence, for approval.' },
  { label: 'Alerts', path: '/insights/alerts', description: 'Anomalies across revenue, conversion, competitions, payments and advertising.' },
  { label: 'Trends', path: '/insights/trends', description: 'Longer-running movements worth knowing about.' },
]

const ANALYTICS_TABS: TabDef[] = [
  { label: 'Business overview', path: '/analytics', end: true, description: 'Headline trading figures for the selected period.' },
  { label: 'Website', path: '/analytics/website', description: 'Visitors, sessions and traffic sources.' },
  { label: 'Advertising', path: '/analytics/advertising', description: 'Meta campaign spend, delivery and provider-attributed results.' },
  { label: 'Funnel', path: '/analytics/funnel', description: 'How visitors move from landing to purchase, stage by stage.' },
  { label: 'Abandonment', path: '/analytics/abandonment', description: 'Where and why people leave: competition, selection, basket, checkout and payment.' },
  { label: 'Competitions', path: '/analytics/competitions', description: 'Sales, conversion, sell-through and pacing per competition.' },
  { label: 'Customers', path: '/analytics/customers', description: 'New and returning customers, value and frequency.' },
  { label: 'Revenue', path: '/analytics/revenue', description: 'Revenue, orders and order value over time.' },
  { label: 'Devices', path: '/analytics/devices', description: 'Conversion by device and browser.' },
  { label: 'Geography', path: '/analytics/geography', description: 'Where customers buy from.' },
]

const MARKETING_TABS: TabDef[] = [
  { label: 'Campaigns', path: '/marketing/campaigns', description: 'Multi-channel initiatives across online and offline channels.' },
  { label: 'Advertising', path: '/marketing/advertising', description: 'Provider-backed ad channels: spend, return and delivery.' },
  { label: 'Channels', path: '/marketing/channels', description: 'Every channel compared, digital and offline alike.' },
  { label: 'Attribution', path: '/marketing/attribution', description: 'Which channels, campaigns and creatives drove each sale.' },
  { label: 'Creatives', path: '/marketing/creatives', description: 'Creative performance across channels.' },
  { label: 'Audiences', path: '/marketing/audiences', description: 'Segments to target, include or exclude.' },
]

const AUTOMATION_TABS: TabDef[] = [
  { label: 'Workflows', path: '/automation/workflows', description: 'Automations and their current state.' },
  { label: 'Triggers', path: '/automation/triggers', description: 'The events and thresholds automations can start from.' },
  { label: 'Scheduled actions', path: '/automation/scheduled', description: 'Actions queued to run at a set time.' },
  { label: 'Activity', path: '/automation/activity', description: 'What ran, when, and what it did.' },
]

const COMPETITION_TABS: TabDef[] = [
  { label: 'Listings', path: '/competitions/listings', description: 'Competition listings, drafted and approved.' },
  { label: 'Publishing', path: '/competitions/publishing', description: 'Where listings are published and what happened.' },
  { label: 'Templates', path: '/competitions/templates', description: 'Reusable listing structures and copy.' },
  { label: 'Performance', path: '/competitions/performance', description: 'Competition analytics.' },
]

const MARKET_TABS: TabDef[] = [
  { label: 'Competitors', path: '/market/competitors', description: 'The operators you track.' },
  { label: 'Competitions', path: '/market/competitions', description: 'Competitions running elsewhere in the market.' },
  { label: 'Prize trends', path: '/market/prizes', description: 'Which prizes and categories are rising or falling.' },
  { label: 'Pricing', path: '/market/pricing', description: 'Ticket prices and entry limits across the market.' },
  { label: 'Activity', path: '/market/activity', description: 'Recent launches, closes and changes.' },
]

const REPORT_TABS: TabDef[] = [
  { label: 'Reports', path: '/reports', end: true, description: 'Saved and scheduled reports.' },
  { label: 'Exports', path: '/reports/exports', permission: 'exports.create', description: 'Data exports you have requested.' },
]

const INTEGRATION_TABS: TabDef[] = [
  { label: 'Integrations', path: '/integrations', end: true, description: 'Connected providers and their sync status.' },
]

const SETTINGS_TABS: TabDef[] = [
  { label: 'Branding', path: '/settings/branding', permission: 'settings.manage', description: 'Name, logo, locale and sender identity.' },
  { label: 'Users', path: '/settings/users', permission: 'users.manage', description: 'Who has access, and with which role.' },
  { label: 'Audit log', path: '/settings/audit', permission: 'settings.manage', description: 'Every audited action in this organisation.' },
  { label: 'Entitlements', path: '/settings/entitlements', permission: 'settings.manage', description: 'Which modules this organisation has.' },
  { label: 'Domains', path: '/settings/domains', permission: 'settings.manage', description: 'The custom domain Competition Insights is served on.' },
  { label: 'Websites', path: '/settings/websites', permission: 'integrations.manage', description: 'Site credentials the WordPress connector authenticates with.' },
  { label: 'Tracking', path: '/settings/tracking', permission: 'analytics.read', description: 'The day your website’s tracking is reliable from.' },
]

export const SECTIONS: readonly SectionDef[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: 'overview',
    prefix: '/',
    entitlement: 'core_analytics',
    permission: 'analytics.read',
    description: 'What you need to know right now.',
    tabs: [],
    pages: [{ path: '/', title: 'Overview', kind: 'landing' }],
  },
  {
    key: 'insights',
    label: 'Insights',
    icon: 'insights',
    prefix: '/insights',
    entitlement: 'insights',
    permission: 'analytics.read',
    description: 'What happened, why, and what to do about it.',
    tabs: INSIGHTS_TABS,
    pages: [
      { path: '/insights', title: 'Digest', kind: 'landing', tab: '/insights/digest', description: INSIGHTS_TABS[0]?.description },
      ...tabPages(INSIGHTS_TABS),
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: 'analytics',
    prefix: '/analytics',
    entitlement: 'core_analytics',
    permission: 'analytics.read',
    description: 'What happened on your website and in your sales.',
    tabs: ANALYTICS_TABS,
    pages: [
      ...tabPages(ANALYTICS_TABS, '/analytics'),
      { path: '/analytics/competitions/:id', title: 'Competition', kind: 'detail', tab: '/analytics/competitions' },
      { path: '/analytics/customers/:id', title: 'Customer', kind: 'detail', tab: '/analytics/customers' },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    icon: 'marketing',
    prefix: '/marketing',
    entitlement: 'marketing_intelligence',
    permission: 'analytics.read',
    description: 'Campaigns and channels, online and offline, and what they returned.',
    tabs: MARKETING_TABS,
    pages: [
      ...tabPages(MARKETING_TABS),
      { path: '/marketing/campaigns/new', title: 'New campaign', kind: 'create', tab: '/marketing/campaigns', permission: 'campaigns.manage' },
      { path: '/marketing/campaigns/:id', title: 'Campaign', kind: 'detail', tab: '/marketing/campaigns' },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    icon: 'automation',
    prefix: '/automation',
    entitlement: 'automation',
    permission: 'analytics.read',
    description: 'What the platform does for you, and when.',
    tabs: AUTOMATION_TABS,
    pages: [
      ...tabPages(AUTOMATION_TABS),
      { path: '/automation/workflows/:id', title: 'Workflow', kind: 'detail', tab: '/automation/workflows' },
    ],
  },
  {
    key: 'competitions',
    label: 'Competition Management',
    icon: 'competitions',
    prefix: '/competitions',
    entitlement: 'competition_operations',
    permission: 'analytics.read',
    description: 'Create, publish and manage competition listings.',
    tabs: COMPETITION_TABS,
    pages: [
      ...tabPages(COMPETITION_TABS).map((page) =>
        page.path === '/competitions/performance'
          ? { ...page, kind: 'redirect' as const, redirectTo: '/analytics/competitions' }
          : page,
      ),
      // Creating a listing is a marketing-manager action until `listings.manage` exists.
      { path: '/competitions/listings/new', title: 'New listing', kind: 'create', tab: '/competitions/listings', permission: 'campaigns.manage' },
      { path: '/competitions/listings/:id', title: 'Listing', kind: 'detail', tab: '/competitions/listings' },
    ],
  },
  {
    key: 'market',
    label: 'Market Intelligence',
    icon: 'market',
    prefix: '/market',
    entitlement: 'market_intelligence',
    permission: 'analytics.read',
    description: 'What competitors are running, and at what price.',
    tabs: MARKET_TABS,
    pages: [
      ...tabPages(MARKET_TABS),
      { path: '/market/competitors/:id', title: 'Competitor', kind: 'detail', tab: '/market/competitors' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: 'reports',
    prefix: '/reports',
    entitlement: 'core_analytics',
    permission: 'analytics.read',
    description: 'Reports and exports.',
    tabs: REPORT_TABS,
    pages: [
      ...tabPages(REPORT_TABS, '/reports'),
      { path: '/reports/:id', title: 'Report', kind: 'detail', tab: '/reports' },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: 'integrations',
    prefix: '/integrations',
    entitlement: null,
    permission: 'integrations.manage',
    description: 'The providers Competition Insights reads from and acts through.',
    tabs: INTEGRATION_TABS,
    pages: [
      ...tabPages(INTEGRATION_TABS, '/integrations'),
      { path: '/integrations/:provider', title: 'Integration', kind: 'detail', tab: '/integrations' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: 'settings',
    prefix: '/settings',
    entitlement: null,
    permission: null,
    description: 'Organisation settings.',
    tabs: SETTINGS_TABS,
    pages: tabPages(SETTINGS_TABS),
  },
]

export const ROUTES: readonly RouteDef[] = SECTIONS.flatMap((section) =>
  section.pages.map((page) => ({ ...page, section })),
)

/** Section roots with no page of their own open their first tab. */
export const SECTION_ALIASES: ReadonlyArray<{ path: string; section: SectionKey }> = [
  { path: '/marketing', section: 'marketing' },
  { path: '/automation', section: 'automation' },
  { path: '/competitions', section: 'competitions' },
  { path: '/market', section: 'market' },
  { path: '/settings', section: 'settings' },
]

export function isInSection(section: SectionDef, pathname: string): boolean {
  if (section.prefix === '/') return pathname === '/'
  return pathname === section.prefix || pathname.startsWith(`${section.prefix}/`)
}

/** The route a location renders; a static segment wins over a parameter. */
export function routeForPath(pathname: string): RouteDef | undefined {
  const matches = ROUTES.filter((route) => matchPath({ path: route.path, end: true }, pathname))
  return matches.find((route) => !route.path.includes(':')) ?? matches[0]
}

/** The tab to mark active for a location, if any. */
export function activeTabFor(pathname: string): string | undefined {
  return routeForPath(pathname)?.tab
}
